import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar } from "../components/ui/calendar";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Droplet, 
  Monitor, 
  Tv, 
  Armchair, 
  Square, 
  Mic, 
  MoreHorizontal,
  Users,
  Send,
  Minus,
  Plus
} from "lucide-react";
import { format, getDay, startOfDay } from "date-fns";
import { es } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RESOURCES = [
  { id: "chairs", label: "Sillas", icon: Armchair, max: 40 },
  { id: "tables", label: "Mesas", icon: Square, max: 3 },
  { id: "water", label: "Botellas de agua", icon: Droplet, max: 30 },
];

const SALONS = [
  { id: "gran_salon", label: "Gran Salón", maxPeople: 40, description: "Capacidad máx. 40 personas" },
  { id: "salon_dialogo", label: "Salón de Diálogo", maxPeople: 10, description: "Capacidad máx. 10 personas" },
  { id: "salon_privado", label: "Salón Privado", maxPeople: 5, description: "Capacidad máx. 5 personas" },
];

const TIME_SLOTS = [
  "12:00", "13:00", "14:00", "15:00", "16:00", 
  "17:00", "18:00", "19:00", "20:00"
];

const QuantityCounter = ({ value, onChange, max, label, icon: Icon }) => (
  <div className="border border-[#E5E5E5] rounded-lg p-4 flex flex-col items-center gap-2 hover:border-[#EA580C] transition-colors bg-white" data-testid={`resource-counter-${label.toLowerCase()}`}>
    <div className="flex items-center gap-2 w-full">
      <Icon className="w-14 h-14 text-[#0A0A0A] flex-shrink-0" strokeWidth={1.2} />
      <span className="text-sm font-bold text-[#0A0A0A]">{label}</span>
    </div>
    <div className="flex items-center gap-1 mt-1">
      <button
        type="button"
        onClick={() => onChange(Math.max(0, value - 1))}
        className="w-9 h-9 flex items-center justify-center rounded-md bg-[#F5F5F5] hover:bg-[#E5E5E5] transition-colors text-[#EA580C]"
        data-testid={`minus-${label.toLowerCase()}`}
      >
        <Minus className="w-4 h-4" />
      </button>
      <input
        type="number"
        min="0"
        max={max}
        value={value}
        onChange={(e) => {
          const v = parseInt(e.target.value) || 0;
          onChange(Math.max(0, Math.min(max, v)));
        }}
        className="w-12 h-9 text-center text-lg font-bold text-[#0A0A0A] border border-[#E5E5E5] rounded-md focus:outline-none focus:border-[#EA580C] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        data-testid={`qty-${label.toLowerCase()}`}
      />
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        className="w-9 h-9 flex items-center justify-center rounded-md bg-[#F5F5F5] hover:bg-[#E5E5E5] transition-colors text-[#EA580C] disabled:opacity-30"
        data-testid={`plus-${label.toLowerCase()}`}
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
    <p className="text-[10px] text-[#999]">M&aacute;x. {max}</p>
  </div>
);

const BookingPage = () => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSalon, setSelectedSalon] = useState("");
  const [timeStart, setTimeStart] = useState("");
  const [timeEnd, setTimeEnd] = useState("");
  const [numPeople, setNumPeople] = useState("");
  const [organizerName, setOrganizerName] = useState("");
  const [referentName, setReferentName] = useState("");
  const [organizerEmail, setOrganizerEmail] = useState("");
  const [organizerPhone, setOrganizerPhone] = useState("");
  const [eventName, setEventName] = useState("");
  const [eventDescription, setEventDescription] = useState("");
  const [resourceQuantities, setResourceQuantities] = useState({
    water: 0, projector: 0, tv: 0, chairs: 0, tables: 0, microphone: 0
  });
  const [hasOther, setHasOther] = useState(false);
  const [otherDescription, setOtherDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookedRanges, setBookedRanges] = useState([]);
  const [blockedRanges, setBlockedRanges] = useState([]);
  const [fullDayBlocked, setFullDayBlocked] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);

  // Fetch globally blocked dates on mount
  useEffect(() => {
    const fetchBlockedDates = async () => {
      try {
        const res = await axios.get(`${API}/blocked-dates`);
        setBlockedDates(res.data.blocked_dates || []);
      } catch { /* ignore */ }
    };
    fetchBlockedDates();
  }, []);

  const fetchAvailability = useCallback(async (date, salon) => {
    if (!date || !salon) { setBookedRanges([]); setBlockedRanges([]); setFullDayBlocked(false); return; }
    try {
      const dateStr = format(date, "yyyy-MM-dd");
      const res = await axios.get(`${API}/reservations/availability/${dateStr}?salon=${salon}`);
      setBookedRanges(res.data.booked_ranges || []);
      setBlockedRanges(res.data.blocked_ranges || []);
      setFullDayBlocked(res.data.full_day_blocked || false);
    } catch {
      setBookedRanges([]);
      setBlockedRanges([]);
      setFullDayBlocked(false);
    }
  }, []);

  useEffect(() => {
    if (selectedDate && selectedSalon) {
      fetchAvailability(selectedDate, selectedSalon);
      setTimeStart("");
      setTimeEnd("");
    } else {
      setBookedRanges([]);
      setBlockedRanges([]);
      setFullDayBlocked(false);
    }
  }, [selectedDate, selectedSalon, fetchAvailability]);

  const currentSalon = SALONS.find(s => s.id === selectedSalon);
  const maxPeople = currentSalon?.maxPeople || 999;

  const isTimeBooked = (time) => {
    return bookedRanges.some(range => time >= range.start && time < range.end);
  };

  const isTimeBlocked = (time) => {
    return blockedRanges.some(range => time >= range.start && time < range.end);
  };

  const isTimeUnavailable = (time) => {
    return isTimeBooked(time) || isTimeBlocked(time);
  };

  const getEndTimeSlots = () => {
    if (!timeStart) return [];
    return TIME_SLOTS.filter(t => t > timeStart);
  };

  const updateResourceQty = (id, value) => {
    setResourceQuantities(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !timeStart || !timeEnd || !numPeople || !organizerName || !organizerEmail || !eventName || !organizerPhone || !referentName || !selectedSalon || !eventDescription.trim()) {
      toast.error("Por favor completa todos los campos requeridos");
      return;
    }

    if (parseInt(numPeople) > maxPeople) {
      toast.error(`El ${currentSalon.label} tiene capacidad máxima de ${maxPeople} personas`);
      return;
    }

    if (timeEnd <= timeStart) {
      toast.error("La hora de fin debe ser después de la hora de inicio");
      return;
    }

    setIsSubmitting(true);

    try {
      const reservationData = {
        organizer_name: organizerName,
        organizer_email: organizerEmail,
        organizer_phone: organizerPhone,
        referent_name: referentName,
        salon: selectedSalon,
        event_name: eventName,
        event_date: format(selectedDate, "yyyy-MM-dd"),
        event_time_start: timeStart,
        event_time_end: timeEnd,
        num_people: parseInt(numPeople),
        event_description: eventDescription || null,
        resources: {
          ...resourceQuantities,
          kitchen: 0,
          other: hasOther,
          other_description: hasOther ? otherDescription : null
        }
      };

      await axios.post(`${API}/reservations`, reservationData);
      
      toast.success("Reservación creada exitosamente", {
        description: `Fecha: ${format(selectedDate, "d 'de' MMMM, yyyy", { locale: es })} de ${timeStart} a ${timeEnd}`
      });

      setSelectedDate(null);
      setSelectedSalon("");
      setTimeStart("");
      setTimeEnd("");
      setNumPeople("");
      setOrganizerName("");
      setReferentName("");
      setOrganizerEmail("");
      setOrganizerPhone("");
      setEventName("");
      setEventDescription("");
      setResourceQuantities({ water: 0, projector: 0, tv: 0, chairs: 0, tables: 0, microphone: 0 });
      setHasOther(false);
      setOtherDescription("");
    } catch (error) {
      console.error("Error creating reservation:", error);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409) {
        toast.error("Horario no disponible", { description: detail });
        fetchAvailability(selectedDate, selectedSalon);
      } else {
        toast.error("Error al crear la reservación", {
          description: "Por favor intenta de nuevo"
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Hero Section - Desktop: imagen vertical (con logo arriba) */}
      <div className="hidden lg:flex lg:w-[45%] xl:w-1/2 sticky top-16 overflow-hidden flex-col bg-[#e8c9a8]" style={{ height: 'calc(100vh - 64px)' }}>
        <img 
          src="https://customer-assets-lxgj4vgw.emergentagent.net/job_politica-reservas/artifacts/rb2k0hqx_57FD8638-3367-43BF-9A07-A3786A4A18E4.jpeg"
          alt="Casa Ciudadana Zapopan - Reserva tu espacio"
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Form Section */}
      <div className="lg:w-[55%] xl:w-1/2 w-full">
        {/* Mobile Hero: banner horizontal */}
        <div className="lg:hidden">
          <img 
            src="https://customer-assets-lxgj4vgw.emergentagent.net/job_politica-reservas/artifacts/97qzzys1_2EC017AF-37ED-491C-AD87-9B4FA15AFA20.jpeg"
            alt="Casa Ciudadana Zapopan - Reserva tu espacio"
            className="w-full h-auto"
          />
        </div>

        <div className="p-5 md:p-8 lg:p-12 xl:p-16 overflow-y-auto">
          <form onSubmit={handleSubmit} className="max-w-xl mx-auto space-y-8" data-testid="booking-form">
            
            {/* Section 1: Organizer Info */}
            <div className="space-y-4">
              <h2 className="heading-h3 flex items-center gap-2 text-[#EA580C]">
                <span className="w-7 h-7 bg-[#EA580C] text-white text-sm font-bold flex items-center justify-center flex-shrink-0">1</span>
                Información del Organizador
              </h2>
              
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label htmlFor="referent" className="label-text">Referente *</Label>
                  <Input
                    id="referent"
                    type="text"
                    value={referentName}
                    onChange={(e) => setReferentName(e.target.value)}
                    placeholder="Nombre del referente"
                    className="input-swiss rounded-none text-sm"
                    data-testid="input-referent"
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="name" className="label-text">Actor Político *</Label>
                  <Input
                    id="name"
                    type="text"
                    value={organizerName}
                    onChange={(e) => setOrganizerName(e.target.value)}
                    placeholder="Nombre del actor político"
                    className="input-swiss rounded-none text-sm"
                    data-testid="input-name"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="phone" className="label-text">Teléfono *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={organizerPhone}
                    onChange={(e) => setOrganizerPhone(e.target.value)}
                    placeholder="Ej. 33 1234 5678"
                    className="input-swiss rounded-none text-sm"
                    data-testid="input-phone"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="email" className="label-text">Correo *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={organizerEmail}
                    onChange={(e) => setOrganizerEmail(e.target.value)}
                    placeholder="ejemplo@correo.com"
                    className="input-swiss rounded-none text-sm"
                    data-testid="input-email"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Event Details */}
            <div className="space-y-4">
              <h2 className="heading-h3 flex items-center gap-2 text-[#EA580C]">
                <span className="w-7 h-7 bg-[#EA580C] text-white text-sm font-bold flex items-center justify-center flex-shrink-0">2</span>
                Detalles del Evento
              </h2>

              {/* Salon Selection FIRST */}
              <div className="space-y-2">
                <Label className="label-text">Tipo de Salón *</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {SALONS.map((salon) => (
                    <button
                      key={salon.id}
                      type="button"
                      onClick={() => { setSelectedSalon(salon.id); setTimeStart(""); setTimeEnd(""); setNumPeople(""); }}
                      className={`border p-4 text-left transition-all ${
                        selectedSalon === salon.id 
                          ? "border-[#EA580C] bg-[#EA580C]/5 ring-2 ring-[#EA580C]" 
                          : "border-[#E5E5E5] hover:border-[#EA580C]"
                      }`}
                      data-testid={`salon-${salon.id}`}
                    >
                      <p className={`text-sm font-bold ${selectedSalon === salon.id ? "text-[#EA580C]" : "text-[#0A0A0A]"}`}>{salon.label}</p>
                      <p className="text-xs text-[#666] mt-1">{salon.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Event Name */}
              <div className="space-y-1">
                <Label htmlFor="eventName" className="label-text">Nombre del Evento *</Label>
                <Input
                  id="eventName"
                  type="text"
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                  placeholder="Ej. Capacitación, Taller, Reunión, etc."
                  className="input-swiss rounded-none text-sm"
                  data-testid="input-event-name"
                  required
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                {/* Calendar */}
                <div className="flex-shrink-0">
                  <div className="border border-[#E5E5E5] inline-block" data-testid="calendar-container">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      locale={es}
                      disabled={(date) => {
                        const today = startOfDay(new Date());
                        if (date < today) return true;
                        if (getDay(date) === 0) return true;
                        const dateStr = format(date, "yyyy-MM-dd");
                        if (blockedDates.includes(dateStr)) return true;
                        return false;
                      }}
                      className="rounded-none"
                      data-testid="booking-calendar"
                    />
                  </div>
                </div>

                {/* Time selectors + People - RIGHT side of calendar */}
                <div className="flex-1 flex flex-col gap-2">
                  <div className="space-y-1">
                    <Label className="label-text text-[10px]">Hora de inicio *</Label>
                    <Select value={timeStart} onValueChange={(val) => { setTimeStart(val); setTimeEnd(""); }} disabled={fullDayBlocked}>
                      <SelectTrigger className="input-swiss rounded-none text-sm h-9" data-testid="time-start-trigger">
                        <SelectValue placeholder={fullDayBlocked ? "Día bloqueado" : "Inicio"} />
                      </SelectTrigger>
                      <SelectContent>
                        {TIME_SLOTS.map((time) => {
                          const booked = isTimeBooked(time);
                          const blocked = isTimeBlocked(time);
                          const unavailable = booked || blocked;
                          return (
                            <SelectItem key={time} value={time} disabled={unavailable} data-testid={`time-start-${time}`}>
                              {time} {booked ? "— Ocupado" : blocked ? "— Bloqueado" : ""}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="label-text text-[10px]">Hora de fin *</Label>
                    <Select value={timeEnd} onValueChange={setTimeEnd} disabled={!timeStart || fullDayBlocked}>
                      <SelectTrigger className="input-swiss rounded-none text-sm h-9" data-testid="time-end-trigger">
                        <SelectValue placeholder="Fin" />
                      </SelectTrigger>
                      <SelectContent>
                        {getEndTimeSlots().map((time) => {
                          const booked = isTimeBooked(time);
                          const blocked = isTimeBlocked(time);
                          const unavailable = booked || blocked;
                          return (
                            <SelectItem key={time} value={time} disabled={unavailable} data-testid={`time-end-${time}`}>
                              {time} {booked ? "— Ocupado" : blocked ? "— Bloqueado" : ""}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="numPeople" className="label-text text-[10px] flex items-center gap-1">
                      <Users className="w-3 h-3" /> Personas * {currentSalon && <span className="text-[#EA580C]">(máx. {maxPeople})</span>}
                    </Label>
                    <Input
                      id="numPeople"
                      type="number"
                      min="1"
                      max={maxPeople}
                      value={numPeople}
                      onChange={(e) => setNumPeople(e.target.value)}
                      placeholder={currentSalon ? `Máx. ${maxPeople}` : "Selecciona salón"}
                      className="input-swiss rounded-none text-sm h-9"
                      data-testid="input-num-people"
                      required
                    />
                  </div>
                  {fullDayBlocked && (
                    <div className="bg-red-50 border border-red-300 p-2" data-testid="blocked-day-warning">
                      <p className="text-[10px] font-semibold text-red-700">Día bloqueado por administrador</p>
                      {blockedRanges[0]?.reason && <p className="text-[10px] text-red-600">{blockedRanges[0].reason}</p>}
                    </div>
                  )}
                  {!fullDayBlocked && blockedRanges.length > 0 && (
                    <div className="bg-red-50 border border-red-300 p-2" data-testid="blocked-ranges-warning">
                      <p className="text-[10px] font-semibold text-red-700">Bloqueado:</p>
                      {blockedRanges.map((range, i) => (
                        <p key={i} className="text-[10px] text-red-600">{range.start} - {range.end} {range.reason ? `(${range.reason})` : ''}</p>
                      ))}
                    </div>
                  )}
                  {bookedRanges.length > 0 && (
                    <div className="bg-[#FEF3C7] border border-yellow-300 p-2" data-testid="booked-warning">
                      <p className="text-[10px] font-semibold text-[#92400E]">Ocupado:</p>
                      {bookedRanges.map((range, i) => (
                        <p key={i} className="text-[10px] text-[#92400E]">{range.start} - {range.end}</p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Section 3: Resources */}
            <div className="space-y-4">
              <h2 className="heading-h3 flex items-center gap-2 text-[#EA580C]">
                <span className="w-7 h-7 bg-[#EA580C] text-white text-sm font-bold flex items-center justify-center flex-shrink-0">3</span>
                Equipamiento
              </h2>
              
              <div className="grid grid-cols-2 gap-3">
                {RESOURCES.map((resource) => (
                  <QuantityCounter
                    key={resource.id}
                    value={resourceQuantities[resource.id]}
                    onChange={(val) => updateResourceQty(resource.id, val)}
                    max={resource.max}
                    label={resource.label}
                    icon={resource.icon}
                  />
                ))}
              </div>

              {/* Other resources */}
              <div className="border border-[#E5E5E5] p-3">
                <label className="flex items-center gap-3 cursor-pointer" data-testid="resource-other">
                  <input
                    type="checkbox"
                    checked={hasOther}
                    onChange={(e) => setHasOther(e.target.checked)}
                    className="w-4 h-4 accent-[#EA580C]"
                  />
                  <MoreHorizontal className="w-5 h-5 text-[#666]" />
                  <span className="text-sm font-semibold">Otras necesidades</span>
                </label>
                {hasOther && (
                  <Input
                    type="text"
                    value={otherDescription}
                    onChange={(e) => setOtherDescription(e.target.value)}
                    placeholder="Describe qué necesitas..."
                    className="input-swiss rounded-none text-sm mt-3"
                    data-testid="input-other-description"
                  />
                )}
              </div>
            </div>

            {/* Description */}
            <div className="space-y-1">
              <Label htmlFor="description" className="label-text">
                Descripción del evento *
              </Label>
              <Textarea
                id="description"
                value={eventDescription}
                onChange={(e) => setEventDescription(e.target.value)}
                placeholder="Describe brevemente el propósito del evento..."
                className="input-swiss rounded-none min-h-[80px] resize-none text-sm"
                data-testid="input-description"
                required
              />
            </div>

            {/* Submit Button */}
            <div className="pb-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#EA580C] text-white py-4 font-bold text-lg uppercase tracking-wide hover:bg-orange-700 transition-colors disabled:bg-[#E5E5E5] disabled:text-[#666] flex items-center justify-center gap-2"
                data-testid="booking-submit"
              >
                <Send className="w-5 h-5" />
                {isSubmitting ? "Enviando..." : "Confirmar Reservación"}
              </button>
              <p className="text-xs text-[#666666] text-center mt-3">
                Al reservar, aceptas los términos y condiciones.
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
